import { useContext, useState } from "react";
import CustomLink from "@Components/CustomLink";
import Reveal from "@Components/Reveal/Reveal";
// The navbar's gradient pill IS this page's CTA — same class, not a copy of its
// rules, so the two can't drift apart. It also carries the `color: #fff
// !important` guard the site's global `a:hover` rule needs.
import navStyles from "@Components/Navbar/Navbar.module.scss";
import InlineLottie from "@Components/InlineLottie/InlineLottie";
import { ScholarshipFormContext } from "@Pages/Scholarship/ScholarshipFormModal";
import DeadlineModal from "../DeadlineModal/DeadlineModal";
import styles from "./ScholarshipHero.module.scss";
import wrapperHOC from "@Utils/wrapperHOC";
import useTranslationHook from "hooks/useTranslationHook";
import { pageAsset } from "@Utils/getStaticAssetPath";

// Images and animations come from the static-assets bucket, never `import`ed — see `pageAsset`.
const asset = pageAsset("ScholarshipV2");

const graduationFees = asset("hero-graduation-fees.json");

/**
 * Scholarship hero — Figma node 2096:5361.
 *
 * Structure and metrics follow the other v2 heroes (About Us / Contact Us / How
 * It Works) rather than the design's own values, so all four pages share one
 * hero rhythm: 56px top padding, a centred 880px header block, 52px/56px title
 * and an 18px/1.5 subtitle 20px below it.
 *
 * The headline used to end on the figure — "deserves [mark] $50,000" — and now ends on
 * "a big break", with the figure moved into the subtitle. The animated mark stays exactly
 * where it was, between "deserves" and whatever follows, so its seating (see `.mark`'s
 * negative vertical-align) is unaffected.
 */
/**
 * `openForm` comes from ScholarshipFormContext rather than a prop: that is the
 * contract the page this replaces used for its Apply buttons, and it keeps the
 * handler out of every component between here and the page.
 */
const Hero = () => {
  const { t } = useTranslationHook();
  const { openForm } = useContext(ScholarshipFormContext);
  const [deadlineOpen, setDeadlineOpen] = useState(false);

  return (
    // `data-sch-hero` is the Navbar's `ctaRevealAfter` sentinel: while this section
    // is still on screen it is showing the Apply Now pill itself, so the bar must not
    // repeat it. Same pattern as [data-lwu-hero] and [data-gb-hero].
    <section className={styles.hero} data-sch-hero>
      <div className={styles.header}>
        <Reveal as="h1" className={styles.title}>
          {t("scholarship.v2.hero.title.line1")}
          <br />
          {t("scholarship.v2.hero.title.line2")}
          <span className={styles.mark}>
            {/* Art scaled 2.2×: the comp's ink fills only 44% of its canvas, so the
              multiplier does the real work and sits the mark at about the figure's
              own height rather than above it.

              `size={56}` is now only the pre-CSS fallback — `.markArt` restates the
              box as 1.08em (56/52) so it tracks the fluid title instead of holding
              56px against a 31px phone headline and prising the line box open. */}
            <InlineLottie
              src={graduationFees}
              size={56}
              scale={2.2}
              delay={550}
              className={styles.markArt}
            />
          </span>
          {t("scholarship.v2.hero.title.line3")}
        </Reveal>
        {/* "$50,000" moved DOWN here from the headline, where it used to be the phrase the
          mark ran into. Emphasised rather than plain: it is the only number on the page and
          the reason anyone reads the rest of the sentence. */}
        <Reveal as="p" className={styles.subtitle} delay={120}>
          {t("scholarship.v2.hero.subtitle.before")}{" "}
          <strong className={styles.amount}>$50,000</strong>
          {t("scholarship.v2.hero.subtitle.after")}
        </Reveal>
      </div>

      <Reveal className={styles.ctaRow} delay={200}>
        <CustomLink
          href="/scholarship"
          onClick={(e: React.MouseEvent) => {
            e.preventDefault();
            openForm();
          }}
          className={navStyles.tryButton}
          dataTestId="scholarship-v2-apply"
        >
          {t("scholarship.v2.applynow")}
        </CustomLink>
      </Reveal>

      {/* Deadline notice, sitting quietly under the button. It opens the notice
          in full — the date moved, and the reasoning behind that is more than a
          hero line can carry.

          A real <button>, not a click handler on the <p>: this is an action, so
          it has to be reachable by keyboard and announced as one. The styling
          keeps it looking like the quiet line it was. */}
      <Reveal as="p" className={styles.note} delay={260}>
        <button
          type="button"
          className={styles.noteButton}
          onClick={() => setDeadlineOpen(true)}
          aria-haspopup="dialog"
          data-testid="scholarship-v2-deadline"
        >
          {/* The extension IS the news, so it is the whole line. "Applications
              open" went with the old date — it was the standing state, and next
              to an extension it read as the headline when it was not. */}
          <span className={styles.noteDeadline}>{t("scholarship.v2.hero.note.deadline")}</span>
          <span className={styles.noteSep} aria-hidden="true">
            |
          </span>
          <span className={styles.noteMore}>
            {t("scholarship.v2.hero.note.learnmore")}
            {/* Drawn rather than imported: one 12px chevron is not worth a file,
                and taking the stroke from currentColor means it tracks the
                label's colour without a second declaration. */}
            <svg
              className={styles.noteChevron}
              viewBox="0 0 16 16"
              fill="none"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M6 3.5L10.5 8L6 12.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        </button>
      </Reveal>

      <DeadlineModal open={deadlineOpen} onClose={() => setDeadlineOpen(false)} />
    </section>
  );
};

export default wrapperHOC(Hero, {
  componentName: "Hero-ScholarshipV2",
  showForChina: true,
});
